import { FC, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import useI18n from "../../../hooks/useTranslations";
import type { BallastType } from "../../../http/border/types/ballastTypes";
import {
  BorderBallastForSettings,
  BorderBaseForSettings,
  BorderGuardrailCompositionForSettings,
  BorderNormativeForSettings,
  BorderRailForSettings,
  BorderSettings,
  BorderSubstructureForSettings,
  BorderSystemForSettings,
  BorderToeBoardForSettings,
  BorderUprightForSettings,
  BorderUprightSpanMappingForSettings,
} from "../../../http/border/types/borderSettings";
import { UprightType } from "../../../http/border/types/uprightTypes";
import { getMinutesRoundedTimestamp } from "../../../utils";
import Button from "../../ui/Button";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import LoadingSpinner from "../../ui/LoadingSpinner";
import Numeric from "../../ui/Numeric";
import { CustomSelect } from "../../ui/select/CustomSelect";
import SelectionConfigurator, {
  SelectionConfiguratorVisualField,
  type SelectionConfiguratorSelectionSections,
} from "../../ui/SelectionConfigurator";
import Switch from "../../ui/Switch";

export interface BorderUprightSelectionFormBody {
  system: BorderSystemForSettings | null;
  normative: BorderNormativeForSettings | null;
  guardrailComposition: BorderGuardrailCompositionForSettings | null;
  upright: BorderUprightForSettings | null;
  ballastType: BallastType | null;
  ballast: BorderBallastForSettings | null;
  substructure: BorderSubstructureForSettings | null;
  base: BorderBaseForSettings | null;
  baseCorrugationSpan: number | null;
  span: number | null;
  rail: BorderRailForSettings | null;
  intermediateRail: BorderRailForSettings | null;
  hasCurb: boolean;
  curbHeight: number | null;
  curbDepth: number | null;
}

interface BorderUprightSelectionProps {
  defaultValues?: BorderUprightSelectionFormBody;
  settings?: BorderSettings;
  isLoading?: boolean;
  next: () => void;
  back: () => void;
  onSubmit: (body: BorderUprightSelectionFormBody) => Promise<void> | undefined;
  isAdmin?: boolean;
  canEdit?: boolean;
}

type VisualSelectionField =
  "system" | "normative" | "guardrailComposition" | "uprightType" | "ballastType" | "ballast" | "base" | "substructure";

type UprightHeightOption = {
  height: number;
  label: string;
};

type BaseDistanceOption = {
  distance: number;
  label: string;
};

type BaseCorrugationSpanOption = {
  corrugationSpan: number;
  label: string;
};

const imageUrl = (image?: string | null) =>
  image ? `${import.meta.env.VITE_BUCKET_URL}${image}?${getMinutesRoundedTimestamp()}` : undefined;

const nameLabel = (option?: { name?: string | null } | null) => option?.name ?? "";

const idValue = (option?: { id?: string | null } | null) => option?.id ?? "";

const itemCodeLabel = (option?: { item?: { code?: string | null } | null } | null) => option?.item?.code ?? "";

const itemIDValue = (option?: { item?: { id?: string | null } | null } | null) => option?.item?.id ?? "";

const uniqueByID = <T extends { id: string }>(items: T[]) =>
  items.filter((item, index) => items.findIndex((x) => x.id === item.id) === index);

const uniqueUprights = (items: BorderUprightForSettings[]) =>
  items.filter((item, index) => items.findIndex((x) => x.item.id === item.item.id) === index);

const uniqueByItemID = <T extends { item?: { id?: string | null } | null }>(items: T[]) =>
  items.filter((item, index) => {
    const itemID = itemIDValue(item);
    return !!itemID && items.findIndex((x) => itemIDValue(x) === itemID) === index;
  });

export const resolveBorderRail = (
  rails: BorderRailForSettings[] | undefined,
  diameter: number | null | undefined,
  reinforcedRailRequired: boolean | undefined,
  preferredRailId?: string | null,
) => {
  if (!rails?.length || !diameter) {
    return null;
  }

  const compatibleRails = rails.filter(
    (rail) => rail.diameter === diameter && rail.isReinforced === !!reinforcedRailRequired,
  );

  return compatibleRails.find((rail) => rail.item.id === preferredRailId) ?? compatibleRails[0] ?? null;
};

export const resolveBorderToeBoard = (
  toeBoards: BorderToeBoardForSettings[] | undefined,
  toeBoardRequired: boolean | undefined,
  preferredToeBoardId?: string | null,
) => {
  if (!toeBoards?.length || !toeBoardRequired) {
    return null;
  }

  return toeBoards.find((toeBoard) => toeBoard.item.id === preferredToeBoardId) ?? toeBoards[0] ?? null;
};

const getSystemSpanMappings = (
  settings: BorderSettings | undefined,
  system: BorderSystemForSettings | null | undefined,
  normative: BorderNormativeForSettings | null | undefined,
) =>
  (settings?.uprightSpanMappings ?? []).filter(
    (x) => x.systemNormative.system.id === system?.id && x.systemNormative.normative.id === normative?.id,
  );

const getUprightSpanMappings = (
  settings: BorderSettings | undefined,
  system: BorderSystemForSettings | null | undefined,
  normative: BorderNormativeForSettings | null | undefined,
  upright: BorderUprightForSettings | null | undefined,
) => getSystemSpanMappings(settings, system, normative).filter((x) => x.upright.item.id === upright?.item.id);

const getIsBallastVisible = (
  settings: BorderSettings | undefined,
  system: BorderSystemForSettings | null | undefined,
  normative: BorderNormativeForSettings | null | undefined,
  upright: BorderUprightForSettings | null | undefined,
) =>
  !!system?.isBallastRequired ||
  getSystemSpanMappings(settings, system, normative).some(
    (x) => (!upright || x.upright.item.id === upright.item.id) && !!x.ballast,
  );

const getIsSubstructureVisible = (
  settings: BorderSettings | undefined,
  system: BorderSystemForSettings | null | undefined,
  normative: BorderNormativeForSettings | null | undefined,
  upright: BorderUprightForSettings | null | undefined,
) =>
  !!system?.isSubstructureRequired ||
  getSystemSpanMappings(settings, system, normative).some(
    (x) => (!upright || x.upright.item.id === upright.item.id) && !!x.substructure,
  );

const findSpanMapping = (
  settings: BorderSettings | undefined,
  system: BorderSystemForSettings | null | undefined,
  normative: BorderNormativeForSettings | null | undefined,
  guardrailComposition: BorderGuardrailCompositionForSettings | null | undefined,
  upright: BorderUprightForSettings | null | undefined,
  ballast: BorderBallastForSettings | null | undefined,
  substructure: BorderSubstructureForSettings | null | undefined,
  base: BorderBaseForSettings | null | undefined,
  baseCorrugationSpan: number | null | undefined,
): BorderUprightSpanMappingForSettings | undefined => {
  const ballastVisible = getIsBallastVisible(settings, system, normative, upright);
  const substructureVisible = getIsSubstructureVisible(settings, system, normative, upright);
  const hasCompatibleUprightMapping = settings?.uprightMappings?.some(
    (x) =>
      x.systemNormative.system.id === system?.id &&
      x.systemNormative.normative.id === normative?.id &&
      x.guardrailComposition.id === guardrailComposition?.id &&
      x.upright.item.id === upright?.item.id,
  );

  if (!hasCompatibleUprightMapping) {
    return undefined;
  }

  if (
    (system?.isBallastRequired && !ballast) ||
    (system?.isSubstructureRequired && !substructure) ||
    (!base && baseCorrugationSpan == null)
  ) {
    return undefined;
  }

  const baseID = itemIDValue(base);
  const matches =
    settings?.uprightSpanMappings?.filter(
      (x) =>
        x.systemNormative.system.id === system?.id &&
        x.systemNormative.normative.id === normative?.id &&
        x.upright.item.id === upright?.item.id &&
        (!ballastVisible || (ballast ? x.ballast?.item.id === ballast.item.id : !x.ballast)) &&
        (!substructureVisible || (substructure ? x.substructure?.id === substructure.id : !x.substructure)) &&
        (base ? !!baseID && itemIDValue(x.base) === baseID : x.base?.corrugationSpan === baseCorrugationSpan),
    ) ?? [];

  return matches[0];
};

const resolveBaseSelectionForSubstructure = ({
  settings,
  uprightSpanMappings,
  isBallastVisible,
  selectedBallast,
  isSubstructureVisible,
  selectedSubstructure,
  selectedBase,
  selectedBaseCorrugationSpan,
}: {
  settings: BorderSettings | undefined;
  uprightSpanMappings: BorderUprightSpanMappingForSettings[];
  isBallastVisible: boolean;
  selectedBallast: BorderBallastForSettings | null | undefined;
  isSubstructureVisible: boolean;
  selectedSubstructure: BorderSubstructureForSettings | null | undefined;
  selectedBase: BorderBaseForSettings | null | undefined;
  selectedBaseCorrugationSpan: number | null | undefined;
}): { base: BorderBaseForSettings | null; baseCorrugationSpan: number | null } => {
  const bases = uniqueByItemID(
    uprightSpanMappings
      .filter(
        (mapping) =>
          !isBallastVisible ||
          (selectedBallast ? mapping.ballast?.item.id === selectedBallast.item.id : !mapping.ballast),
      )
      .filter(
        (mapping) =>
          !isSubstructureVisible ||
          (selectedSubstructure ? mapping.substructure?.id === selectedSubstructure.id : !mapping.substructure),
      )
      .map((mapping) => mapping.base)
      .filter((base): base is BorderBaseForSettings => !!base)
      .map((base) => settings?.bases?.find((x) => itemIDValue(x) === itemIDValue(base)) ?? base),
  );

  if (!bases.length) {
    return { base: null, baseCorrugationSpan: null };
  }

  const selectedBaseID = itemIDValue(selectedBase);
  const corrugationSpanOptions = Array.from(
    new Set(bases.map((base) => base.corrugationSpan).filter((corrugationSpan) => corrugationSpan != null)),
  );

  if (!corrugationSpanOptions.length) {
    const base =
      selectedBaseID && bases.some((base) => itemIDValue(base) === selectedBaseID)
        ? (selectedBase ?? null)
        : (bases[0] ?? null);

    return { base, baseCorrugationSpan: null };
  }

  const preferredCorrugationSpan = selectedBaseCorrugationSpan ?? selectedBase?.corrugationSpan ?? null;
  const baseCorrugationSpan =
    preferredCorrugationSpan != null && corrugationSpanOptions.includes(preferredCorrugationSpan)
      ? preferredCorrugationSpan
      : corrugationSpanOptions[0]!;

  const matchingBases = uniqueByItemID(bases.filter((base) => base.corrugationSpan === baseCorrugationSpan));
  const base =
    selectedBaseID && matchingBases.some((base) => itemIDValue(base) === selectedBaseID)
      ? (selectedBase ?? null)
      : matchingBases.length === 1
        ? (matchingBases[0] ?? null)
        : null;

  return { base, baseCorrugationSpan };
};

const BorderUprightSelection: FC<BorderUprightSelectionProps> = ({
  defaultValues,
  settings,
  isLoading,
  next,
  back,
  onSubmit,
  isAdmin,
  canEdit,
}: BorderUprightSelectionProps) => {
  const i18n = useI18n();
  const [activeVisualField, setActiveVisualField] = useState<VisualSelectionField | null>("system");
  const [selectedUprightTypeID, setSelectedUprightTypeID] = useState<string | null>(null);
  const [selectedUprightHeight, setSelectedUprightHeight] = useState<number | null>(null);
  const [explicitBallastSelectionID, setExplicitBallastSelectionID] = useState<string | null>(null);

  return (
    <Form<BorderUprightSelectionFormBody>
      mode={"onBlur"}
      defaultValues={defaultValues}
      disabled={isLoading}
      fullWidth
      onSubmit={() => async (formData) =>
        isAdmin ? await onSubmit(formData) : canEdit ? await onSubmit(formData)?.then(() => next()) : next()
      }
      submitText={isAdmin ? i18n.translation.common.save : i18n.translation.common.next}
      secondaryButton={
        <>
          {isLoading && <LoadingSpinner delayed={0} size="small" />}
          <Button
            buttonProps={{
              disabled: isLoading,
              onClick: back,
            }}
            type="secondary"
          >
            {i18n.translation.common.back}
          </Button>
        </>
      }
    >
      {({ setValue, watch, control, reset }) => {
        const selectedSystem = watch("system");
        const selectedNormative = watch("normative");
        const selectedGuardrailComposition = watch("guardrailComposition");
        const selectedUpright = watch("upright");
        const selectedBallastType = watch("ballastType");
        const selectedBallast = watch("ballast");
        const selectedSubstructure = watch("substructure");
        const selectedBase = watch("base");
        const selectedBaseCorrugationSpan = watch("baseCorrugationSpan");
        const selectedRail = watch("rail");
        const selectedIntermediateRail = watch("intermediateRail");
        const selectedHasCurb = watch("hasCurb");
        const normatives =
          selectedSystem && settings?.systemNormatives.length
            ? settings.systemNormatives.filter((x) => x.system.id === selectedSystem.id).map((x) => x.normative)
            : (settings?.normatives ?? []);
        const systemSpanMappings = getSystemSpanMappings(settings, selectedSystem, selectedNormative);
        const uprightSpanMappings = getUprightSpanMappings(
          settings,
          selectedSystem,
          selectedNormative,
          selectedUpright,
        );
        const isBallastVisible = getIsBallastVisible(settings, selectedSystem, selectedNormative, selectedUpright);
        const isSubstructureVisible = getIsSubstructureVisible(
          settings,
          selectedSystem,
          selectedNormative,
          selectedUpright,
        );
        const isCurbVisible = !!selectedSystem?.isCurbVisible && !selectedGuardrailComposition?.isToeBoardRequired;
        const isCurbDepthVisible = !!selectedSystem?.isCurbDepthVisible;
        const resolvedRail = resolveBorderRail(
          settings?.rails,
          selectedUpright?.upperHoleDiameter,
          selectedNormative?.reinforcedRailRequired,
          selectedRail?.item.id,
        );
        const resolvedIntermediateRail = selectedGuardrailComposition?.isIntermediateBarRequired
          ? resolveBorderRail(
              settings?.rails,
              selectedUpright?.lowerHoleDiameter,
              selectedNormative?.reinforcedRailRequired,
              selectedIntermediateRail?.item.id,
            )
          : null;

        const guardrailCompositions =
          selectedSystem && selectedNormative
            ? uniqueByID(
                settings?.uprightMappings
                  .filter(
                    (x) =>
                      x.systemNormative.system.id === selectedSystem?.id &&
                      x.systemNormative.normative.id === selectedNormative?.id,
                  )
                  .map((x) => x.guardrailComposition) ?? [],
              )
            : (settings?.guardrailCompositions ?? []);

        const systemNormativeUprightMappings =
          selectedSystem && selectedNormative
            ? ((settings?.uprightMappings ?? []).filter(
                (x) =>
                  x.systemNormative.system.id === selectedSystem?.id &&
                  x.systemNormative.normative.id === selectedNormative?.id,
              ) ?? [])
            : [];

        const uprightMappings = selectedGuardrailComposition
          ? systemNormativeUprightMappings.filter((x) => x.guardrailComposition.id === selectedGuardrailComposition.id)
          : systemNormativeUprightMappings;

        const uprightIdsWithSpanMappings = new Set(systemSpanMappings.map((x) => x.upright.item.id));
        const availableUprights = uniqueUprights(uprightMappings.map((x) => x.upright)).filter((upright) =>
          uprightIdsWithSpanMappings.has(upright.item.id),
        );

        const availableTypeIDs = new Set(availableUprights.map((upright) => upright.type.id));
        const uprightTypeOptions = settings?.uprightTypes.filter((type) => availableTypeIDs.has(type.id));
        const selectedUprightTypeOption =
          uprightTypeOptions?.find((option) => option.id === selectedUprightTypeID) ?? null;
        const selectedUprightType = selectedUprightTypeOption ?? null;
        const uprightHeightOptions = selectedUprightType
          ? Array.from(
              new Set(
                availableUprights
                  .filter((upright) => upright.type.id === selectedUprightType.id)
                  .map((upright) => upright.height),
              ),
            )
              .sort((a, b) => a - b)
              .map((height): UprightHeightOption => ({ height, label: `${height} mm` }))
          : [];
        const selectedUprightHeightOption =
          uprightHeightOptions.find((option) => option.height === selectedUprightHeight) ?? null;
        const availableUprightsKey = availableUprights
          .map((upright) => `${upright.item.id}:${upright.type.id}`)
          .join("|");

        const availableBallasts = isBallastVisible
          ? uniqueByItemID(
              uprightSpanMappings
                .map((x) => x.ballast)
                .filter((ballast): ballast is BorderBallastForSettings => !!ballast),
            )
          : [];
        const ballastTypes = uniqueByID(
          availableBallasts
            .map((ballast) => ballast.ballastType)
            .filter((ballastType): ballastType is BallastType => !!ballastType),
        );
        const selectedBallastTypeID = idValue(selectedBallastType);
        const selectedBallastTypeOption =
          ballastTypes.find((ballastType) => ballastType.id === selectedBallastTypeID) ?? null;
        const ballasts = selectedBallastTypeID
          ? availableBallasts.filter((ballast) => ballast.ballastType?.id === selectedBallastTypeID)
          : [];
        const selectedBallastID = itemIDValue(selectedBallast);
        const selectedBallastOption =
          ballasts.length === 1 || selectedBallastID === explicitBallastSelectionID
            ? (ballasts.find((ballast) => itemIDValue(ballast) === selectedBallastID) ?? null)
            : null;
        const shouldSelectBallast = !!selectedBallastTypeID && ballasts.length > 1;
        const ballastTypesKey = ballastTypes.map(idValue).join("|");
        const ballastsKey = ballasts.map(itemIDValue).join("|");

        const substructureMappings = uprightSpanMappings.filter(
          (x) => !isBallastVisible || !selectedBallastOption || x.ballast?.item.id === selectedBallastOption.item.id,
        );

        const substructures = isSubstructureVisible
          ? uniqueByID(
              substructureMappings
                .map((x) => x.substructure)
                .filter((substructure): substructure is BorderSubstructureForSettings => !!substructure),
            )
          : [];
        const substructuresKey = substructures.map(idValue).join("|");

        const baseMappings = uprightSpanMappings
          .filter(
            (x) =>
              !isBallastVisible ||
              (selectedBallastOption ? x.ballast?.item.id === selectedBallastOption.item.id : !x.ballast),
          )
          .filter(
            (x) =>
              !isSubstructureVisible ||
              (selectedSubstructure ? x.substructure?.id === selectedSubstructure.id : !x.substructure),
          );

        const bases = uniqueByItemID(
          baseMappings
            .map((x) => x.base)
            .filter((base): base is BorderBaseForSettings => !!base)
            .map((base) => settings?.bases?.find((x) => itemIDValue(x) === itemIDValue(base)) ?? base),
        );
        const baseDistanceOptions: BaseDistanceOption[] = Array.from(new Set(bases.map((base) => base.distance)))
          .sort((a, b) => a - b)
          .map((distance) => ({ distance, label: `${distance} mm` }));
        const baseCorrugationSpanOptions: BaseCorrugationSpanOption[] = Array.from(
          new Set(
            bases
              .map((base) => base.corrugationSpan)
              .filter((corrugationSpan): corrugationSpan is number => corrugationSpan != null),
          ),
        )
          .sort((a, b) => a - b)
          .map((corrugationSpan) => ({ corrugationSpan, label: `${corrugationSpan} mm` }));
        const selectedBaseDistanceOption =
          baseDistanceOptions.find((option) => option.distance === selectedBase?.distance) ?? null;
        const selectedBaseCorrugationSpanOption =
          baseCorrugationSpanOptions.find((option) => option.corrugationSpan === selectedBaseCorrugationSpan) ?? null;
        const isBaseDisabled = !!selectedSystem?.isSubstructureRequired && !selectedSubstructure;
        const shouldSelectBaseCorrugationSpan = baseCorrugationSpanOptions.length > 0;
        const shouldSelectBaseDistance = !shouldSelectBaseCorrugationSpan && baseDistanceOptions.length > 1;
        const basesKey = bases
          .map((base) => `${itemIDValue(base)}:${base.distance}:${base.corrugationSpan ?? ""}`)
          .join("|");
        const matchingBasesForSelectedCorrugationSpan =
          selectedBaseCorrugationSpan != null
            ? uniqueByItemID(bases.filter((base) => base.corrugationSpan === selectedBaseCorrugationSpan))
            : [];
        const selectedBaseForSpanMapping =
          shouldSelectBaseCorrugationSpan && matchingBasesForSelectedCorrugationSpan.length > 1 ? null : selectedBase;
        const selectedSpanMapping = findSpanMapping(
          settings,
          selectedSystem,
          selectedNormative,
          selectedGuardrailComposition,
          selectedUpright,
          selectedBallastOption,
          selectedSubstructure,
          selectedBaseForSpanMapping,
          selectedBaseCorrugationSpan,
        );
        const selectedUprightMapping = uprightMappings.find((x) => x.upright.item.id === selectedUpright?.item.id);
        const minCurbHeight = selectedUprightMapping?.minCurbHeight ?? null;
        const canShowCurb = isCurbVisible && minCurbHeight != null;
        const resolvedCurbBaseHeight = selectedSpanMapping?.base?.height ?? null;
        const baseSummaryValue = shouldSelectBaseCorrugationSpan
          ? selectedBaseCorrugationSpan != null
            ? `${selectedBaseCorrugationSpan} mm`
            : "-"
          : itemCodeLabel(selectedBase) || "-";

        const clearUprightSelection = () => {
          setSelectedUprightTypeID(null);
          setSelectedUprightHeight(null);
        };

        const syncUprightSelection = (upright: BorderUprightForSettings | null | undefined) => {
          if (!upright) {
            clearUprightSelection();
            return;
          }

          setSelectedUprightTypeID(upright.type.id);
          setSelectedUprightHeight(upright.height);
        };

        const clearBaseSelection = () => {
          setValue("base", null);
          setValue("baseCorrugationSpan", null);
        };

        const selectHasCurb = (value: boolean) => {
          setValue("hasCurb", value);
          if (!value) {
            setValue("curbHeight", null);
            setValue("curbDepth", null);
          }
        };

        const selectSystem = (value: BorderSystemForSettings | null) => {
          setValue("system", value);
          setValue("normative", null);
          setValue("guardrailComposition", null);
          setValue("upright", null);
          setValue("ballastType", null);
          setValue("ballast", null);
          setExplicitBallastSelectionID(null);
          setValue("substructure", null);
          clearBaseSelection();
          setValue("span", null);
          setValue("rail", null);
          setValue("intermediateRail", null);
          clearUprightSelection();
        };

        const selectNormative = (value: BorderNormativeForSettings | null) => {
          setValue("normative", value);
          setValue("guardrailComposition", null);
          setValue("upright", null);
          setValue("ballastType", null);
          setValue("ballast", null);
          setExplicitBallastSelectionID(null);
          setValue("substructure", null);
          clearBaseSelection();
          setValue("span", null);
          setValue("rail", null);
          setValue("intermediateRail", null);
          clearUprightSelection();
        };

        const selectGuardrailComposition = (value: BorderGuardrailCompositionForSettings | null) => {
          setValue("guardrailComposition", value);
          setValue("upright", null);
          setValue("ballastType", null);
          setValue("ballast", null);
          setExplicitBallastSelectionID(null);
          setValue("substructure", null);
          clearBaseSelection();
          setValue("span", null);
          setValue("rail", null);
          setValue("intermediateRail", null);
          clearUprightSelection();
        };

        const selectUpright = (value: BorderUprightForSettings | null) => {
          setValue("upright", value, { shouldValidate: true });
          setValue("ballastType", null);
          setValue("ballast", null);
          setExplicitBallastSelectionID(null);
          setValue("substructure", null);
          clearBaseSelection();
          setValue("span", null);
          setValue("rail", null);
          setValue("intermediateRail", null);
        };

        const selectUprightType = (value: UprightType | null) => {
          setSelectedUprightTypeID(value?.id ?? null);
          setSelectedUprightHeight(null);
          selectUpright(null);
          setActiveVisualField(value ? null : "uprightType");
        };

        const selectUprightHeight = (value: UprightHeightOption | null) => {
          const height = value?.height ?? null;
          const resolvedUpright =
            height === null || !selectedUprightType
              ? null
              : (availableUprights.find(
                  (upright) => upright.type.id === selectedUprightType.id && upright.height === height,
                ) ?? null);

          setSelectedUprightHeight(height);
          selectUpright(resolvedUpright);
        };

        const selectBallastType = (value: BallastType | null) => {
          const nextBallasts = value ? availableBallasts.filter((ballast) => ballast.ballastType?.id === value.id) : [];

          setValue("ballastType", value, { shouldValidate: true });
          setValue("ballast", nextBallasts.length === 1 ? nextBallasts[0]! : null);
          setExplicitBallastSelectionID(null);
          setValue("substructure", null);
          clearBaseSelection();
          setValue("rail", null);
          setValue("intermediateRail", null);
          setActiveVisualField(value && nextBallasts.length > 1 ? "ballast" : value ? null : "ballastType");
        };

        const selectBallast = (value: BorderBallastForSettings | null, isExplicitSelection = true) => {
          setValue("ballast", value);
          setExplicitBallastSelectionID(isExplicitSelection ? itemIDValue(value) || null : null);
          setValue("substructure", null);
          clearBaseSelection();
          setValue("rail", null);
          setValue("intermediateRail", null);
        };

        const selectSubstructure = (value: BorderSubstructureForSettings | null) => {
          const nextBaseSelection = resolveBaseSelectionForSubstructure({
            settings,
            uprightSpanMappings,
            isBallastVisible,
            selectedBallast: selectedBallastOption,
            isSubstructureVisible,
            selectedSubstructure: value,
            selectedBase,
            selectedBaseCorrugationSpan,
          });
          const nextSpanMapping = findSpanMapping(
            settings,
            selectedSystem,
            selectedNormative,
            selectedGuardrailComposition,
            selectedUpright,
            selectedBallastOption,
            value,
            nextBaseSelection.base,
            nextBaseSelection.baseCorrugationSpan,
          );

          setValue("substructure", value);
          setValue("base", nextBaseSelection.base);
          setValue("baseCorrugationSpan", nextBaseSelection.baseCorrugationSpan);
          setValue("span", nextSpanMapping?.span ?? null);
          setValue("rail", null);
          setValue("intermediateRail", null);
        };

        const selectBase = (value: BorderBaseForSettings | null) => {
          setValue("base", value);
          setValue("baseCorrugationSpan", value?.corrugationSpan ?? null);
          setValue("rail", null);
          setValue("intermediateRail", null);
        };

        const selectBaseDistance = (value: BaseDistanceOption | null) => {
          const resolvedBase = value ? (bases.find((base) => base.distance === value.distance) ?? null) : null;

          setValue("base", resolvedBase);
          setValue("baseCorrugationSpan", null);
          setValue("rail", null);
          setValue("intermediateRail", null);
        };

        const selectBaseCorrugationSpan = (value: BaseCorrugationSpanOption | null) => {
          const corrugationSpan = value?.corrugationSpan ?? null;
          const matchingBases =
            corrugationSpan != null
              ? uniqueByItemID(bases.filter((base) => base.corrugationSpan === corrugationSpan))
              : [];

          setValue("baseCorrugationSpan", corrugationSpan, { shouldValidate: true });
          setValue("base", matchingBases.length === 1 ? matchingBases[0]! : null);
          setValue("rail", null);
          setValue("intermediateRail", null);
        };

        useEffect(() => {
          if (defaultValues) {
            reset(defaultValues);
            syncUprightSelection(defaultValues.upright);
            setExplicitBallastSelectionID(itemIDValue(defaultValues.ballast) || null);
          }
        }, [defaultValues]);

        useEffect(() => {
          if (selectedUpright) {
            syncUprightSelection(selectedUpright);
          }
        }, [selectedUpright?.item.id, selectedUpright?.type.id, selectedUpright?.height]);

        useEffect(() => {
          if (selectedUpright && !availableUprights.some((upright) => upright.item.id === selectedUpright.item.id)) {
            selectUpright(null);
            clearUprightSelection();
          }
        }, [availableUprightsKey, selectedUpright?.item.id]);

        useEffect(() => {
          if (selectedUprightTypeID && !uprightTypeOptions?.some((option) => option.id === selectedUprightTypeID)) {
            clearUprightSelection();
          }
        }, [availableUprightsKey, selectedUprightTypeID]);

        useEffect(() => {
          if (!canShowCurb && selectedHasCurb) {
            setValue("hasCurb", false);
            setValue("curbHeight", null);
            setValue("curbDepth", null);
          }
        }, [canShowCurb, selectedHasCurb]);

        useEffect(() => {
          setValue("span", selectedSpanMapping?.span ?? null);
        }, [selectedSpanMapping?.id]);

        useEffect(() => {
          if (selectedRail?.item.id !== resolvedRail?.item.id) {
            setValue("rail", resolvedRail);
          }
        }, [resolvedRail?.item.id, selectedRail?.item.id]);

        useEffect(() => {
          if (selectedIntermediateRail?.item.id !== resolvedIntermediateRail?.item.id) {
            setValue("intermediateRail", resolvedIntermediateRail);
          }
        }, [resolvedIntermediateRail?.item.id, selectedIntermediateRail?.item.id]);

        useEffect(() => {
          if (
            selectedBallastType &&
            (!selectedBallastTypeID || !ballastTypes.some((x) => idValue(x) === selectedBallastTypeID))
          ) {
            setValue("ballastType", null);
            setValue("ballast", null);
            setExplicitBallastSelectionID(null);
            setValue("substructure", null);
            clearBaseSelection();
            setValue("rail", null);
            setValue("intermediateRail", null);
          }
        }, [ballastTypesKey, selectedBallastType?.id]);

        useEffect(() => {
          if (!selectedBallastTypeID) {
            if (selectedBallast) {
              setValue("ballast", null);
              setExplicitBallastSelectionID(null);
              setValue("substructure", null);
              clearBaseSelection();
              setValue("rail", null);
              setValue("intermediateRail", null);
            }
            return;
          }

          if (ballasts.length === 1) {
            const ballast = ballasts[0]!;
            if (selectedBallastID !== itemIDValue(ballast)) {
              selectBallast(ballast, false);
            }
            return;
          }

          if (
            selectedBallast &&
            (!selectedBallastID || selectedBallastID !== explicitBallastSelectionID || !selectedBallastOption)
          ) {
            setValue("ballast", null);
            setExplicitBallastSelectionID(null);
            setValue("substructure", null);
            clearBaseSelection();
            setValue("rail", null);
            setValue("intermediateRail", null);
          }
        }, [ballastsKey, selectedBallastType?.id, selectedBallast?.item?.id, explicitBallastSelectionID]);

        useEffect(() => {
          const selectedSubstructureID = idValue(selectedSubstructure);
          if (
            selectedSubstructure &&
            (!selectedSubstructureID || !substructures.some((x) => idValue(x) === selectedSubstructureID))
          ) {
            setValue("substructure", null);
            clearBaseSelection();
            setValue("rail", null);
            setValue("intermediateRail", null);
          }
        }, [substructuresKey, selectedSubstructure?.id]);

        useEffect(() => {
          const selectedBaseID = itemIDValue(selectedBase);
          if (selectedBase && (!selectedBaseID || !bases.some((x) => itemIDValue(x) === selectedBaseID))) {
            if (shouldSelectBaseCorrugationSpan) {
              setValue("base", null);
            } else {
              clearBaseSelection();
            }
            setValue("rail", null);
            setValue("intermediateRail", null);
          }
        }, [basesKey, shouldSelectBaseCorrugationSpan, selectedBase?.item?.id]);

        useEffect(() => {
          if (
            selectedBaseCorrugationSpan != null &&
            !baseCorrugationSpanOptions.some((x) => x.corrugationSpan === selectedBaseCorrugationSpan)
          ) {
            clearBaseSelection();
            setValue("rail", null);
            setValue("intermediateRail", null);
          }
        }, [basesKey, selectedBaseCorrugationSpan]);

        useEffect(() => {
          if (!shouldSelectBaseCorrugationSpan || selectedBaseCorrugationSpan == null) {
            return;
          }

          const resolvedBase =
            matchingBasesForSelectedCorrugationSpan.length === 1 ? matchingBasesForSelectedCorrugationSpan[0]! : null;

          if (itemIDValue(selectedBase) !== itemIDValue(resolvedBase)) {
            setValue("base", resolvedBase);
          }
        }, [basesKey, shouldSelectBaseCorrugationSpan, selectedBaseCorrugationSpan, selectedBase?.item?.id]);

        useEffect(() => {
          if (
            !isBaseDisabled &&
            !shouldSelectBaseCorrugationSpan &&
            !shouldSelectBaseDistance &&
            bases.length === 1 &&
            itemIDValue(selectedBase) !== itemIDValue(bases[0])
          ) {
            selectBase(bases[0]!);
          }
        }, [
          basesKey,
          isBaseDisabled,
          shouldSelectBaseCorrugationSpan,
          shouldSelectBaseDistance,
          selectedBase?.item?.id,
        ]);

        const selectionSections: SelectionConfiguratorSelectionSections = {
          system: {
            groupTitle: i18n.translation.systems.singular,
            cards: (settings?.systems ?? []).map((system) => ({
              key: idValue(system),
              label: nameLabel(system),
              imageSrc: imageUrl(system.image),
              selected: selectedSystem?.id === system.id,
              onClick: () => selectSystem(system),
            })),
          },
          normative: {
            groupTitle: i18n.translation.normatives.singular,
            cards: normatives.map((normative) => ({
              key: idValue(normative),
              label: nameLabel(normative),
              imageSrc: imageUrl(normative.image),
              selected: selectedNormative?.id === normative.id,
              onClick: () => selectNormative(normative),
            })),
          },
          guardrailComposition: {
            groupTitle: "Guardrail composition",
            cards: guardrailCompositions.map((composition) => ({
              key: idValue(composition),
              label: nameLabel(composition),
              imageSrc: imageUrl(composition.image),
              selected: selectedGuardrailComposition?.id === composition.id,
              onClick: () => selectGuardrailComposition(composition),
            })),
          },
          uprightType: {
            groupTitle: i18n.translation.uprightTypes.singular,
            cards: (uprightTypeOptions ?? []).map((uprightType) => ({
              key: idValue(uprightType),
              label: nameLabel(uprightType),
              imageSrc: imageUrl(uprightType.image),
              selected: selectedUprightTypeID === uprightType.id,
              onClick: () => selectUprightType(uprightType),
            })),
          },
          ballastType: {
            groupTitle: i18n.translation.ballastTypes.singular,
            cards: ballastTypes.map((ballastType) => ({
              key: idValue(ballastType),
              label: nameLabel(ballastType),
              imageSrc: imageUrl(ballastType.image),
              selected: selectedBallastType?.id === ballastType.id,
              onClick: () => selectBallastType(ballastType),
            })),
          },
          ballast: {
            groupTitle: i18n.translation.ballasts.singular,
            cards: ballasts.map((ballast) => ({
              key: idValue(ballast.item),
              label: nameLabel(ballast.item),
              imageSrc: imageUrl(ballast.item.image),
              selected: selectedBallastOption?.item.id === ballast.item.id,
              onClick: () => selectBallast(ballast),
            })),
          },
          substructure: {
            groupTitle: i18n.translation.substructures.singular,
            cards: substructures.map((substructure) => ({
              key: substructure.id,
              label: substructure.name,
              imageSrc: imageUrl(substructure.image),
              selected: selectedSubstructure?.id == substructure.id,
              onClick: () => selectSubstructure(substructure),
            })),
          },
          base: (() => {
            if (shouldSelectBaseCorrugationSpan) {
              return {
                groupTitle: i18n.translation.common.corrugationSpan,
                cards: baseCorrugationSpanOptions.map((corrugationSpanOption) => ({
                  key: `${corrugationSpanOption.corrugationSpan}`,
                  label: corrugationSpanOption.label,
                  selected: selectedBaseCorrugationSpan === corrugationSpanOption.corrugationSpan,
                  onClick: () => selectBaseCorrugationSpan(corrugationSpanOption),
                })),
              };
            }

            if (shouldSelectBaseDistance) {
              return {
                groupTitle: i18n.translation.common.distance,
                cards: baseDistanceOptions.map((distanceOption) => ({
                  key: `${distanceOption.distance}`,
                  label: distanceOption.label,
                  selected: selectedBaseDistanceOption?.distance === distanceOption.distance,
                  onClick: () => selectBaseDistance(distanceOption),
                })),
              };
            }

            return {
              groupTitle: i18n.translation.bases.singular,
              cards: bases.map((base) => ({
                key: idValue(base.item),
                label: nameLabel(base.item),
                imageSrc: imageUrl(base.item.image),
                selected: selectedBase?.item.id === base.item.id,
                onClick: () => selectBase(base),
              })),
            };
          })(),
        };

        return (
          <SelectionConfigurator
            form={
              <>
                <Controller
                  name="system"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.systems.singular} error={fieldState.error}>
                      {({ labelId }) => (
                        <SelectionConfiguratorVisualField
                          active={activeVisualField === "system"}
                          disabled={isLoading || !(settings?.systems ?? []).length}
                          onOpenSelection={() => setActiveVisualField("system")}
                        >
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={settings?.systems ?? []}
                            placeholder={`${i18n.translation.common.select}...`}
                            isDisabled={field.disabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={idValue}
                            getOptionLabel={nameLabel}
                            isInvalid={fieldState.invalid}
                            onFocus={() => setActiveVisualField("system")}
                            onMenuOpen={() => setActiveVisualField("system")}
                            onChange={(value) => {
                              selectSystem(value ?? null);
                            }}
                          />
                        </SelectionConfiguratorVisualField>
                      )}
                    </FormField>
                  )}
                />
                <Controller
                  name="normative"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.normatives.singular} error={fieldState.error}>
                      {({ labelId }) => (
                        <SelectionConfiguratorVisualField
                          active={activeVisualField === "normative"}
                          disabled={isLoading || !selectedSystem || !normatives.length}
                          onOpenSelection={() => setActiveVisualField("normative")}
                        >
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={normatives}
                            placeholder={`${i18n.translation.common.select}...`}
                            isDisabled={field.disabled || isLoading || !canEdit || !selectedSystem}
                            isLoading={isLoading}
                            getOptionValue={idValue}
                            getOptionLabel={nameLabel}
                            isInvalid={fieldState.invalid}
                            onFocus={() => setActiveVisualField("normative")}
                            onMenuOpen={() => setActiveVisualField("normative")}
                            onChange={(value) => {
                              selectNormative(value ?? null);
                            }}
                          />
                        </SelectionConfiguratorVisualField>
                      )}
                    </FormField>
                  )}
                />
                <Controller
                  name="guardrailComposition"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label="Guardrail composition" error={fieldState.error}>
                      {({ labelId }) => (
                        <SelectionConfiguratorVisualField
                          active={activeVisualField === "guardrailComposition"}
                          disabled={isLoading || !guardrailCompositions.length}
                          onOpenSelection={() => setActiveVisualField("guardrailComposition")}
                        >
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={guardrailCompositions}
                            placeholder={`${i18n.translation.common.select}...`}
                            isDisabled={field.disabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={idValue}
                            getOptionLabel={nameLabel}
                            isInvalid={fieldState.invalid}
                            onFocus={() => setActiveVisualField("guardrailComposition")}
                            onMenuOpen={() => setActiveVisualField("guardrailComposition")}
                            onChange={(value) => {
                              selectGuardrailComposition(value ?? null);
                            }}
                          />
                        </SelectionConfiguratorVisualField>
                      )}
                    </FormField>
                  )}
                />
                <Controller
                  name="upright"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <>
                      <FormField label="Upright type" error={!selectedUprightType ? fieldState.error : undefined}>
                        {({ labelId }) => (
                          <SelectionConfiguratorVisualField
                            active={activeVisualField === "uprightType"}
                            disabled={isLoading || !uprightTypeOptions?.length}
                            onOpenSelection={() => setActiveVisualField("uprightType")}
                          >
                            <CustomSelect
                              inputId={labelId}
                              value={selectedUprightTypeOption}
                              options={uprightTypeOptions}
                              placeholder={`${i18n.translation.common.select}...`}
                              isDisabled={field.disabled || isLoading || !canEdit || !uprightTypeOptions?.length}
                              isLoading={isLoading}
                              getOptionValue={(x) => x.id}
                              getOptionLabel={(x) => x.name}
                              isInvalid={fieldState.invalid && !selectedUprightTypeOption}
                              onFocus={() => setActiveVisualField("uprightType")}
                              onMenuOpen={() => setActiveVisualField("uprightType")}
                              onChange={(value) => {
                                selectUprightType(value ?? null);
                              }}
                            />
                          </SelectionConfiguratorVisualField>
                        )}
                      </FormField>

                      <FormField label="Upright height" error={selectedUprightType ? fieldState.error : undefined}>
                        {({ labelId }) => (
                          <CustomSelect
                            inputId={labelId}
                            value={selectedUprightHeightOption}
                            options={uprightHeightOptions}
                            placeholder={`${i18n.translation.common.select}...`}
                            isDisabled={field.disabled || isLoading || !canEdit || !selectedUprightType}
                            isLoading={isLoading}
                            getOptionValue={(x) => `${x.height}`}
                            getOptionLabel={(x) => x.label}
                            isInvalid={fieldState.invalid && !!selectedUprightType}
                            onFocus={() => setActiveVisualField(null)}
                            onMenuOpen={() => setActiveVisualField(null)}
                            onChange={(value) => {
                              selectUprightHeight(value ?? null);
                            }}
                          />
                        )}
                      </FormField>
                    </>
                  )}
                />

                {isBallastVisible && (
                  <>
                    <Controller
                      name="ballastType"
                      control={control}
                      rules={{ required: !!selectedSystem?.isBallastRequired }}
                      render={({ field, fieldState }) => (
                        <FormField label={i18n.translation.ballastTypes.singular} error={fieldState.error}>
                          {({ labelId }) => (
                            <SelectionConfiguratorVisualField
                              active={activeVisualField === "ballastType"}
                              disabled={isLoading || !ballastTypes.length}
                              onOpenSelection={() => setActiveVisualField("ballastType")}
                            >
                              <CustomSelect
                                {...field}
                                inputId={labelId}
                                value={selectedBallastTypeOption}
                                options={ballastTypes}
                                placeholder={`${i18n.translation.common.select}...`}
                                isDisabled={field.disabled || isLoading || !canEdit || !ballastTypes.length}
                                isLoading={isLoading}
                                getOptionValue={idValue}
                                getOptionLabel={nameLabel}
                                isInvalid={fieldState.invalid}
                                onFocus={() => setActiveVisualField("ballastType")}
                                onMenuOpen={() => setActiveVisualField("ballastType")}
                                onChange={(value) => {
                                  selectBallastType(value ?? null);
                                }}
                              />
                            </SelectionConfiguratorVisualField>
                          )}
                        </FormField>
                      )}
                    />
                    {shouldSelectBallast && (
                      <Controller
                        name="ballast"
                        control={control}
                        rules={{ required: !!selectedSystem?.isBallastRequired || !!selectedBallastTypeID }}
                        render={({ field, fieldState }) => (
                          <FormField label={i18n.translation.ballasts.singular} error={fieldState.error}>
                            {({ labelId }) => (
                              <SelectionConfiguratorVisualField
                                active={activeVisualField === "ballast"}
                                disabled={isLoading || !selectedBallastTypeID || !ballasts.length}
                                onOpenSelection={() => setActiveVisualField("ballast")}
                              >
                                <CustomSelect
                                  {...field}
                                  inputId={labelId}
                                  value={selectedBallastOption}
                                  options={ballasts}
                                  placeholder={`${i18n.translation.common.select}...`}
                                  isLoading={isLoading}
                                  getOptionValue={itemIDValue}
                                  getOptionLabel={itemCodeLabel}
                                  isInvalid={fieldState.invalid}
                                  onFocus={() => setActiveVisualField("ballast")}
                                  onMenuOpen={() => setActiveVisualField("ballast")}
                                  onChange={(value) => {
                                    selectBallast(value ?? null);
                                  }}
                                  isDisabled={field.disabled || isLoading || !canEdit || !selectedBallastTypeID}
                                />
                              </SelectionConfiguratorVisualField>
                            )}
                          </FormField>
                        )}
                      />
                    )}
                  </>
                )}
                {isSubstructureVisible && (
                  <Controller
                    name="substructure"
                    control={control}
                    rules={{ required: !!selectedSystem?.isSubstructureRequired }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.substructures.singular} error={fieldState.error}>
                        {({ labelId }) => (
                          <SelectionConfiguratorVisualField
                            active={activeVisualField === "substructure"}
                            disabled={isLoading || !substructures.length}
                            onOpenSelection={() => setActiveVisualField("substructure")}
                          >
                            <CustomSelect
                              {...field}
                              inputId={labelId}
                              options={substructures}
                              placeholder={`${i18n.translation.common.select}...`}
                              isDisabled={field.disabled || isLoading || !canEdit}
                              isLoading={isLoading}
                              getOptionValue={idValue}
                              getOptionLabel={nameLabel}
                              isInvalid={fieldState.invalid}
                              onFocus={() => setActiveVisualField("substructure")}
                              onMenuOpen={() => setActiveVisualField("substructure")}
                              onChange={(value) => {
                                selectSubstructure(value ?? null);
                              }}
                            />
                          </SelectionConfiguratorVisualField>
                        )}
                      </FormField>
                    )}
                  />
                )}
                {shouldSelectBaseCorrugationSpan && (
                  <Controller
                    name="baseCorrugationSpan"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.corrugationSpan} error={fieldState.error}>
                        {({ labelId }) => (
                          <SelectionConfiguratorVisualField
                            active={activeVisualField === "base"}
                            disabled={isLoading || !baseCorrugationSpanOptions.length || isBaseDisabled}
                            onOpenSelection={() => setActiveVisualField("base")}
                          >
                            <CustomSelect
                              inputId={labelId}
                              value={selectedBaseCorrugationSpanOption}
                              options={baseCorrugationSpanOptions}
                              placeholder={`${i18n.translation.common.select}...`}
                              isDisabled={field.disabled || isLoading || !canEdit || isBaseDisabled}
                              isLoading={isLoading}
                              getOptionValue={(x) => `${x.corrugationSpan}`}
                              getOptionLabel={(x) => x.label}
                              isInvalid={fieldState.invalid}
                              onFocus={() => setActiveVisualField("base")}
                              onMenuOpen={() => setActiveVisualField("base")}
                              onChange={(value) => {
                                selectBaseCorrugationSpan(value ?? null);
                              }}
                            />
                          </SelectionConfiguratorVisualField>
                        )}
                      </FormField>
                    )}
                  />
                )}
                {shouldSelectBaseDistance && (
                  <Controller
                    name="base"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.distance} error={fieldState.error}>
                        {({ labelId }) => (
                          <SelectionConfiguratorVisualField
                            active={activeVisualField === "base"}
                            disabled={isLoading || !baseDistanceOptions.length || isBaseDisabled}
                            onOpenSelection={() => setActiveVisualField("base")}
                          >
                            <CustomSelect
                              inputId={labelId}
                              value={selectedBaseDistanceOption}
                              options={baseDistanceOptions}
                              placeholder={`${i18n.translation.common.select}...`}
                              isDisabled={field.disabled || isLoading || !canEdit || isBaseDisabled}
                              isLoading={isLoading}
                              getOptionValue={(x) => `${x.distance}`}
                              getOptionLabel={(x) => x.label}
                              isInvalid={fieldState.invalid}
                              onFocus={() => setActiveVisualField("base")}
                              onMenuOpen={() => setActiveVisualField("base")}
                              onChange={(value) => {
                                selectBaseDistance(value ?? null);
                              }}
                            />
                          </SelectionConfiguratorVisualField>
                        )}
                      </FormField>
                    )}
                  />
                )}
                {canShowCurb && (
                  <>
                    <Controller
                      name="hasCurb"
                      control={control}
                      render={({ field }) => (
                        <FormField label={i18n.translation.common.curb} isOptional>
                          {({ labelId }) => (
                            <Switch
                              id={labelId}
                              checked={!!field.value}
                              disabled={isLoading || !canEdit}
                              onChange={(checked) => selectHasCurb(checked)}
                            />
                          )}
                        </FormField>
                      )}
                    />
                    {selectedHasCurb && (
                      <>
                        <Controller
                          name="curbHeight"
                          control={control}
                          rules={{
                            required: true,
                            validate: (value) =>
                              minCurbHeight == null ||
                              (value ?? 0) >= minCurbHeight ||
                              i18n.translation.common.curbHeightBelowMinimum(minCurbHeight),
                          }}
                          render={({ field, fieldState }) => (
                            <FormField label={i18n.translation.common.curbHeight} error={fieldState.error}>
                              {({ labelId, isInvalid }) => (
                                <Numeric
                                  {...field}
                                  id={labelId}
                                  isInvalid={isInvalid}
                                  decimalScale={0}
                                  allowNegative={false}
                                  suffix=" mm"
                                  disabled={isLoading || !canEdit}
                                  onBlur={(value) => setValue("curbHeight", value, { shouldValidate: true })}
                                />
                              )}
                            </FormField>
                          )}
                        />
                        {isCurbDepthVisible && (
                          <Controller
                            name="curbDepth"
                            control={control}
                            rules={{
                              required: true,
                              validate: (value) =>
                                resolvedCurbBaseHeight == null ||
                                (value ?? 0) >= resolvedCurbBaseHeight ||
                                i18n.translation.common.curbDepthBelowBaseHeight(resolvedCurbBaseHeight),
                            }}
                            render={({ field, fieldState }) => (
                              <FormField label={i18n.translation.common.curbDepth} error={fieldState.error}>
                                {({ labelId, isInvalid }) => (
                                  <Numeric
                                    {...field}
                                    id={labelId}
                                    isInvalid={isInvalid}
                                    decimalScale={0}
                                    allowNegative={false}
                                    suffix=" mm"
                                    disabled={isLoading || !canEdit}
                                    onBlur={(value) => setValue("curbDepth", value, { shouldValidate: true })}
                                  />
                                )}
                              </FormField>
                            )}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            }
            selectionSections={selectionSections}
            activeSelectionKey={activeVisualField}
            selectionDisabled={isLoading || !canEdit}
            summary={{
              title: "Summary",
              items: [
                {
                  label: i18n.translation.uprights.singular,
                  value: itemCodeLabel(selectedUpright) || "-",
                },
                {
                  label: i18n.translation.ballasts.singular,
                  value: itemCodeLabel(selectedBallastOption) || "-",
                  hidden: !isBallastVisible,
                },
                {
                  label: "Top rail",
                  value: itemCodeLabel(resolvedRail) || "-",
                },
                {
                  label: "Intermediate rail",
                  value: itemCodeLabel(resolvedIntermediateRail) || "-",
                  hidden: !selectedGuardrailComposition?.isIntermediateBarRequired,
                },
                {
                  label: i18n.translation.bases.singular,
                  value: baseSummaryValue,
                },
                {
                  label: i18n.translation.common.span,
                  value: selectedSpanMapping?.span ?? "-",
                },
                {
                  label: i18n.translation.common.nrRivets,
                  value: selectedSpanMapping?.nrRivets ?? "-",
                  hidden: selectedSpanMapping?.nrRivets == null || selectedSpanMapping?.nrRivets == 0,
                },
                {
                  label: i18n.translation.common.nrBallastTerminal,
                  value: selectedSpanMapping?.nrBallastTerminal ?? "-",
                  hidden: !isBallastVisible,
                },
                {
                  label: i18n.translation.common.nrBallastIntermediate,
                  value: selectedSpanMapping?.nrBallastInt ?? "-",
                  hidden: !isBallastVisible,
                },
              ],
            }}
          />
        );
      }}
    </Form>
  );
};

export default BorderUprightSelection;
